import type { QueryClient } from '@tanstack/react-query';
import type {
  Bookmark,
  BookmarkCollectionResponse,
  BookmarkContentResponse,
  BookmarkResponse,
} from '../types/api';
import type { client } from './apiClient';

export type BookmarksParams = Parameters<typeof client.bookmarks>[0];

/** Parse-derived fields that may update after content is fetched. */
const PARSED_BOOKMARK_FIELDS = [
  'title',
  'description',
  'og_image',
  'author',
  'pubtime',
  'reading_time',
  'estimated_time',
  'estimated_total_time',
  'estimated_time_remaining',
] as const satisfies ReadonlyArray<keyof Bookmark>;

/**
 * Content-payload fields available before the bookmark refetch resolves.
 * Subset of PARSED_BOOKMARK_FIELDS — keep in sync with contentHasFresherBookmarkData.
 */
const CONTENT_PARSED_FIELDS = ['title', 'og_image'] as const satisfies ReadonlyArray<
  (typeof PARSED_BOOKMARK_FIELDS)[number]
>;

type ContentParsedFields = Pick<
  BookmarkContentResponse,
  (typeof CONTENT_PARSED_FIELDS)[number]
>;

export function getHomeBookmarksParams(
  page = 1,
  sort?: 'newest' | 'oldest',
): BookmarksParams {
  return {
    section_type: 'home',
    page,
    sort: sort ?? getBookmarkListSortOrder(),
  };
}

export function getBookmarkListSortOrder(): 'newest' | 'oldest' {
  try {
    const saved = localStorage.getItem('ipt:preferences');
    if (saved) {
      const parsed = JSON.parse(saved) as {
        bookmarkList?: { sortOrder?: string };
      };
      const sort = parsed.bookmarkList?.sortOrder;
      if (sort === 'newest' || sort === 'oldest') {
        return sort;
      }
    }
  } catch {
    // Ignore malformed preferences
  }
  return 'newest';
}

export function seedBookmarksCache(
  queryClient: QueryClient,
  params: BookmarksParams,
  data: BookmarkCollectionResponse,
) {
  queryClient.setQueryData(['bookmarks', params], data);
}

/**
 * True when parsed content carries title/thumbnail data the cached bookmark
 * is still missing — e.g. an x.com save that stored an empty title until parse.
 */
export function contentHasFresherBookmarkData(
  bookmark: Bookmark,
  content: ContentParsedFields,
): boolean {
  if (content.title && content.title !== bookmark.title) {
    return true;
  }
  if (content.og_image && !bookmark.og_image) {
    return true;
  }
  return false;
}

function pickParsedBookmarkFields(bookmark: Bookmark): Partial<Bookmark> {
  const picked: Partial<Bookmark> = {};
  for (const key of PARSED_BOOKMARK_FIELDS) {
    const value = bookmark[key];
    if (value !== undefined) {
      Object.assign(picked, { [key]: value });
    }
  }
  return picked;
}

function pickParsedFieldsFromContent(content: ContentParsedFields): Partial<Bookmark> {
  const picked: Partial<Bookmark> = {};
  for (const key of CONTENT_PARSED_FIELDS) {
    const value = content[key];
    if (value) {
      Object.assign(picked, { [key]: value });
    }
  }
  return picked;
}

function applyParsedFieldsToListCaches(
  queryClient: QueryClient,
  bookmarkIdString: string,
  parsedFields: Partial<Bookmark>,
) {
  queryClient.setQueriesData<BookmarkCollectionResponse>(
    {
      predicate: (query) => {
        const key = query.queryKey;
        return Array.isArray(key) && (key[0] === 'bookmarks' || key[0] === 'search');
      },
    },
    (old) => {
      if (!old?.bookmarks) return old;
      let changed = false;
      const bookmarks = old.bookmarks.map((row) => {
        if (String(row.id) !== bookmarkIdString) return row;
        changed = true;
        return { ...row, ...parsedFields };
      });
      return changed ? { ...old, bookmarks } : old;
    },
  );
}

function applyParsedFieldsToCaches(
  queryClient: QueryClient,
  bookmarkId: string,
  parsedFields: Partial<Bookmark>,
  seedBookmark?: Bookmark,
) {
  if (Object.keys(parsedFields).length === 0) return;

  const bookmarkIdString = String(bookmarkId);

  queryClient.setQueryData(
    ['bookmark', bookmarkIdString],
    (old: BookmarkResponse | undefined) => {
      if (!old?.bookmark) {
        if (!seedBookmark) return old;
        return { bookmark: { ...seedBookmark, ...parsedFields } };
      }
      return {
        ...old,
        bookmark: { ...old.bookmark, ...parsedFields },
      };
    },
  );

  applyParsedFieldsToListCaches(queryClient, bookmarkIdString, parsedFields);
}

/**
 * Optimistically merge title/og_image from parsed content into bookmark caches
 * so the reader and list update before the bookmark refetch completes.
 */
export function mergeParsedFieldsFromContent(
  queryClient: QueryClient,
  bookmarkId: string,
  content: ContentParsedFields,
) {
  applyParsedFieldsToCaches(
    queryClient,
    bookmarkId,
    pickParsedFieldsFromContent(content),
  );
}

/**
 * Merge parse-derived fields from an authoritative bookmark record into the
 * detail cache and every list/search cache that holds that row. Only allowlisted
 * fields are written so in-flight optimistic archive/like/tag updates survive.
 */
export function mergeParsedBookmarkFields(queryClient: QueryClient, bookmark: Bookmark) {
  applyParsedFieldsToCaches(
    queryClient,
    String(bookmark.id),
    pickParsedBookmarkFields(bookmark),
    bookmark,
  );
}

/**
 * Merge parse-derived fields into list/search caches only. Use after a bookmark
 * detail refetch, which already replaced `['bookmark', id]`.
 */
export function mergeParsedBookmarkFieldsIntoLists(
  queryClient: QueryClient,
  bookmark: Bookmark,
) {
  const parsedFields = pickParsedBookmarkFields(bookmark);
  if (Object.keys(parsedFields).length === 0) return;
  applyParsedFieldsToListCaches(queryClient, String(bookmark.id), parsedFields);
}
